var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createNestablePublicClientApplication, InteractionRequiredAuthError, } from "@azure/msal-browser";
import { msalConfig, GRAPH_SCOPES, ADDIN_BASE_URL, CLIENT_ID, TENANT_ID, SILENT_REDIRECT_PATH, } from "./msalConfig";
let msalInstance = null;
function getMsalInstance() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!msalInstance) {
            msalInstance = yield createNestablePublicClientApplication(msalConfig);
        }
        return msalInstance;
    });
}
/**
 * Main token acquisition function.
 * Tries three strategies in order:
 *
 * 1. Silent MSAL with cached account (NAA on modern Outlook)
 * 2. Office SSO bootstrap token → extract login hint → ssoSilent
 * 3. Dialog-based interactive consent (fallback for older builds / no prior consent)
 */
export function acquireGraphToken() {
    return __awaiter(this, arguments, void 0, function* (options = {}) {
        const msal = yield getMsalInstance();
        const { interactive = false } = options;
        try {
            const result = yield acquireTokenSilent(msal);
            return result.accessToken;
        }
        catch (silentError) {
            if (silentError instanceof InteractionRequiredAuthError) {
                if (!interactive) {
                    throw new Error("Interactive sign-in required. Click Try Again to continue.");
                }
                if (shouldPreferPopupAuth()) {
                    const popupResult = yield acquireTokenViaPopup(msal);
                    return popupResult.accessToken;
                }
                try {
                    return yield acquireTokenViaDialog();
                }
                catch (dialogError) {
                    console.warn("[Auth] Office dialog sign-in failed, falling back to MSAL popup.", dialogError);
                    const popupResult = yield acquireTokenViaPopup(msal);
                    return popupResult.accessToken;
                }
            }
            throw silentError;
        }
    });
}
function acquireTokenSilent(msal) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield msal.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
            });
        }
        catch (naaError) {
            // Continue to legacy fallbacks for hosts that don't support NAA yet.
            console.info("[Auth] NAA silent acquisition unavailable, trying legacy fallback.", naaError);
        }
        // First, check if we already have an account in the MSAL cache
        const accounts = msal.getAllAccounts();
        if (accounts.length > 0) {
            return msal.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
                account: accounts[0],
            });
        }
        // No cached account — try to extract login hint from Office SSO bootstrap token
        // and use ssoSilent (which accepts loginHint, unlike acquireTokenSilent)
        const loginHint = yield getOfficeLoginHint();
        if (loginHint) {
            // ssoSilent uses a hidden iframe. In OWA the add-in already runs inside
            // an iframe, and some OWA builds silently drop nested iframes, causing
            // ssoSilent to hang forever. Cap it at 8 s and fall through to the dialog.
            const ssoTimeout = new Promise((_, reject) => setTimeout(() => reject(new InteractionRequiredAuthError("sso_silent_timeout", "ssoSilent timed out")), 8000));
            return Promise.race([
                msal.ssoSilent({
                    scopes: GRAPH_SCOPES,
                    loginHint,
                    redirectUri: `${ADDIN_BASE_URL}${SILENT_REDIRECT_PATH}`,
                }),
                ssoTimeout,
            ]);
        }
        throw new InteractionRequiredAuthError("no_account", "No cached account and no Office login hint");
    });
}
/**
 * Gets the login hint (UPN) from the Office SSO bootstrap token.
 * The bootstrap token is issued for the Office resource — its payload
 * contains the user's UPN which we pass to ssoSilent as a loginHint.
 */
function getOfficeLoginHint() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Do NOT pass allowSignInPrompt/allowConsentPrompt here.
            // In OWA those flags can surface a browser-level OAuth prompt that never
            // resolves (blocked popup), hanging the entire auth flow.  This call is
            // only used to extract the login hint from the bootstrap token, so it
            // must be silent-only and fail fast if SSO is not available.
            const bootstrapToken = yield OfficeRuntime.auth.getAccessToken({
                forMSGraphAccess: true,
            });
            const payload = JSON.parse(atob(bootstrapToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
            return (payload["preferred_username"] ||
                payload["upn"] ||
                payload["email"] ||
                null);
        }
        catch (_a) {
            return null;
        }
    });
}
/**
 * Opens an Office dialog for interactive MSAL login.
 * Used when silent acquisition fails (first run, consent required, MFA, etc.).
 */
function acquireTokenViaDialog() {
    return new Promise((resolve, reject) => {
        var _a;
        if (typeof Office === "undefined" ||
            !((_a = Office.context) === null || _a === void 0 ? void 0 : _a.ui) ||
            typeof Office.context.ui.displayDialogAsync !== "function") {
            reject(new Error("Office dialog sign-in is unavailable in this Outlook session."));
            return;
        }
        const dialogUrl = `${ADDIN_BASE_URL}/taskpane/auth-dialog.html` +
            `?clientId=${encodeURIComponent(CLIENT_ID)}` +
            `&tenantId=${encodeURIComponent(TENANT_ID)}`;
        Office.context.ui.displayDialogAsync(dialogUrl, { height: 60, width: 40 }, (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                reject(new Error(`Dialog failed to open: ${result.error.message}`));
                return;
            }
            const dialog = result.value;
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args) => {
                var _a;
                // Type narrowing: DialogMessageReceived args have 'message', DialogEventReceived have 'error'
                if (!("message" in args))
                    return;
                dialog.close();
                try {
                    const payload = JSON.parse(args.message);
                    if (payload.type === "auth_success" && payload.accessToken) {
                        resolve(payload.accessToken);
                    }
                    else {
                        reject(new Error((_a = payload.error) !== null && _a !== void 0 ? _a : "Auth dialog returned no token"));
                    }
                }
                catch (_b) {
                    reject(new Error("Invalid message from auth dialog"));
                }
            });
            dialog.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
                if (!("error" in args))
                    return;
                dialog.close();
                reject(new Error(`Dialog closed unexpectedly (code: ${args.error})`));
            });
        });
    });
}
function acquireTokenViaPopup(msal) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield msal.acquireTokenPopup({
                scopes: GRAPH_SCOPES,
                redirectUri: `${ADDIN_BASE_URL}/taskpane/index.html`,
            });
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Popup sign-in failed";
            throw new Error(`Popup sign-in failed: ${message}`);
        }
    });
}
function shouldPreferPopupAuth() {
    var _a;
    return (typeof Office !== "undefined" &&
        typeof Office.PlatformType !== "undefined" &&
        ((_a = Office.context) === null || _a === void 0 ? void 0 : _a.platform) === Office.PlatformType.OfficeOnline);
}
export function getCurrentAccount() {
    return __awaiter(this, void 0, void 0, function* () {
        const msal = yield getMsalInstance();
        const accounts = msal.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    });
}
export function signOut() {
    return __awaiter(this, void 0, void 0, function* () {
        const msal = yield getMsalInstance();
        const account = msal.getAllAccounts()[0];
        if (account) {
            yield msal.logoutPopup({ account });
        }
    });
}
